<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router'
import BrandLogo from '../components/BrandLogo.vue'
import MouseGlow from '../components/MouseGlow.vue'
import ParticleBackground from '../components/ParticleBackground.vue'
import { useSiteStore } from '../stores/site'
import { safeContentLink, safeInternalPath } from '../utils/security'

const site = useSiteStore()
const route = useRoute()
const router = useRouter()
const brand = computed(() => site.brand)
const appearance = computed(() => site.theme.appearance || 'dark')
const mobileOpen = ref(false)
const scrolled = ref(false)
const nav = computed(() => (site.site?.navigation || [])
  .filter(item => item.location === 'HEADER')
  .map(item => [item.title, safeInternalPath(item.url, '')])
  .filter(item => item[1]))
const footer = computed(() => site.site?.settings?.footer || {})
const contact = computed(() => site.site?.settings?.contact || {})
const phoneLink = computed(() => safeContentLink(`tel:${String(contact.value.phone || '').replace(/[^+\d() -]/g, '')}`))
const emailLink = computed(() => safeContentLink(`mailto:${String(contact.value.email || '').replace(/[\r\n]/g, '')}`))
const footerTarget = (item: { title: string; path: string }) => {
  const path = safeInternalPath(item.path)
  if (path !== '/about') return path
  if (item.title === '公司介绍') return '/about#story'
  if (item.title === '发展历程') return '/about#timeline'
  return path
}
const scrollToPageTop = () => {
  if (typeof window === 'undefined') return
  window.scrollTo({
    top: 0,
    behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
  })
}
const scrollToFooterAnchor = (hash: string) => {
  if (!hash || typeof document === 'undefined') return
  document.getElementById(hash.slice(1))?.scrollIntoView({
    behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    block: 'start',
  })
}
const navigateFooterService = async (target: string) => {
  const safeTarget = safeInternalPath(target)
  const destination = router.resolve(safeTarget)
  if (route.fullPath === destination.fullPath) {
    scrollToPageTop()
    return
  }
  await router.push(safeTarget)
  await nextTick()
  scrollToPageTop()
}
const navigateFooterAboutLink = async (target: string) => {
  const safeTarget = safeInternalPath(target)
  const destination = router.resolve(safeTarget)
  if (route.fullPath === destination.fullPath) {
    scrollToFooterAnchor(destination.hash)
    return
  }
  await router.push(safeTarget)
}
const updateScrolled = () => { scrolled.value = scrollY > 20 }
onMounted(() => { updateScrolled(); addEventListener('scroll', updateScrolled, { passive: true }) })
onBeforeUnmount(() => removeEventListener('scroll', updateScrolled))
</script>

<template>
  <div class="site-shell" :class="`appearance-${appearance}`" data-allow-mismatch :style="{ '--primary': site.theme.primaryColor, '--secondary': site.theme.secondaryColor }">
    <ParticleBackground />
    <MouseGlow />
    <header class="site-header" :class="{ scrolled }">
      <div class="site-header-inner">
        <RouterLink to="/" class="site-logo" @click="mobileOpen=false"><BrandLogo /></RouterLink>
        <nav class="desktop-nav"><RouterLink v-for="item in nav" :key="item[1]" :to="item[1]">{{ item[0] }}</RouterLink></nav>
        <RouterLink to="/contact" class="header-cta">立即咨询 <span>→</span></RouterLink>
        <button class="mobile-menu-button" :aria-expanded="mobileOpen" aria-label="打开菜单" @click="mobileOpen=!mobileOpen"><span></span><span></span><span></span></button>
      </div>
    </header>
    <nav v-if="mobileOpen" class="mobile-nav"><div class="mobile-nav-inner"><RouterLink v-for="item in nav" :key="item[1]" :to="item[1]" @click="mobileOpen=false">{{ item[0] }}</RouterLink><RouterLink to="/contact" class="mobile-cta" @click="mobileOpen=false">立即咨询</RouterLink></div></nav>
    <main><RouterView :key="route.fullPath" /></main>
    <footer class="site-footer"><div class="footer-container"><div class="footer-main"><div class="footer-brand"><BrandLogo /><p>{{ footer.tagline }}</p></div><div class="footer-column"><h3>服务</h3><RouterLink v-for="item in (footer.serviceLinks || [])" :key="item.title" :to="safeInternalPath(item.path)" @click.prevent="navigateFooterService(item.path)">{{ item.title }}</RouterLink></div><div class="footer-column"><h3>关于</h3><RouterLink v-for="item in (footer.aboutLinks || [])" :key="item.title" :to="footerTarget(item)" @click.prevent="navigateFooterAboutLink(footerTarget(item))">{{ item.title }}</RouterLink></div><div class="footer-column"><h3>联系</h3><RouterLink to="/contact">{{ footer.consultLabel }}</RouterLink><a v-if="contact.phone" :href="phoneLink">{{ contact.phone }}</a><a v-if="contact.email" :href="emailLink">{{ contact.email }}</a><span>{{ contact.address || footer.shortAddress }}</span></div></div><div class="footer-bottom"><span>© {{ footer.copyrightYear }} {{ brand.companyName || site.site?.companyName }} 版权所有</span><span>{{ footer.englishName }}</span></div></div></footer>
  </div>
</template>
